// Pure layout math for the Services orbit — a simplified, single-mode
// ("gallery" coverflow) extraction of the orbit-flip-slider component's
// projection math. No DOM access, no React — safe to unit test directly.

export function degToRad(deg) {
  return (deg * Math.PI) / 180;
}

/**
 * Computes {x, y, scale, zIndex} for each of `count` tiles arranged in a
 * shallow 3D coverflow arc, given a continuous rotation offset in degrees.
 * `layout[i]` corresponds to the i-th service passed to the caller.
 */
export function computeGalleryLayout({
  count,
  rotationOffsetDeg = 0,
  containerWidth,
  containerHeight,
  tileSize,
  tiltDeg = 10,
}) {
  const cx = containerWidth / 2;
  const cy = containerHeight / 2;
  const baseRadius = Math.max((count * tileSize) / (2 * Math.PI) * 0.62, tileSize * 0.9);
  const radius = baseRadius * 1.55;
  const camDist = radius * 1.55;
  const tilt = degToRad(tiltDeg);
  const positionScaleStrength = 0.28;
  const sizeScaleStrength = 0.16;
  const offsetDeg = -90 + rotationOffsetDeg;

  const layout = [];
  for (let i = 0; i < count; i++) {
    const thetaDeg = offsetDeg + (i / count) * 360;
    const theta = degToRad(thetaDeg);
    const px = radius * Math.sin(theta);
    const pz0 = -radius * Math.cos(theta);
    const py = -pz0 * Math.sin(tilt);
    const pz = pz0 * Math.cos(tilt);
    const scaleFactor = Math.max(camDist / (camDist + pz), 0.05);
    const positionScale = 1 + (scaleFactor - 1) * positionScaleStrength;
    const sizeScale = 1 + (scaleFactor - 1) * sizeScaleStrength;

    layout.push({
      x: cx + px * positionScale,
      y: cy + py * positionScale,
      scale: sizeScale,
      zIndex: Math.round(scaleFactor * 1000) + 1,
    });
  }
  return layout;
}

export function getFrontIndex(layout) {
  let frontIndex = 0;
  let maxScale = -Infinity;
  layout.forEach((item, i) => {
    if (item.scale > maxScale) {
      maxScale = item.scale;
      frontIndex = i;
    }
  });
  return frontIndex;
}

export function shortestAngleDelta(fromDeg, toDeg) {
  let delta = (toDeg - fromDeg) % 360;
  if (delta > 180) delta -= 360;
  if (delta < -180) delta += 360;
  return delta;
}

/**
 * Returns a rotation offset (continuous with currentRotationOffsetDeg, not
 * wrapped to [0, 360)) that brings services[index] to the front position,
 * via the shortest possible rotation from the current offset.
 */
export function computeTargetRotationForIndex(index, count, currentRotationOffsetDeg) {
  const step = 360 / count;
  const centerDeg = 90 - index * step;
  const currentMod = ((currentRotationOffsetDeg % 360) + 360) % 360;
  const centerMod = ((centerDeg % 360) + 360) % 360;
  const delta = shortestAngleDelta(currentMod, centerMod);
  return currentRotationOffsetDeg + delta;
}
